import { useQuery } from '@tanstack/react-query';
import { ChangeEventHandler } from 'react';

import { getAgreementDetailsApi } from '@flipster/apis/v1/agreement';
import { AgreementDetailType, GetAgreementDetailResponse } from '@flipster/apis/v1/agreement/types';
import { useOverlay } from '@flipster/overlay';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { Styles } from '@flipster/styled-system/css';

import PolicyInformationModal from './PolicyInformationModal';

const injectedText = (
  { agreementText, urls }: GetAgreementDetailResponse,
  handleClick: (id: string, url: string) => void,
): React.ReactNode[] | undefined =>
  urls?.reduce<React.ReactNode[]>(
    (acc, { id, linkWord, url }) =>
      acc.flatMap((node) =>
        typeof node !== 'string'
          ? [node]
          : node.split(linkWord).flatMap((part, idx) => {
              const nodes: React.ReactNode[] = [];

              if (idx > 0)
                nodes.push(
                  <button type='button' key={url} onClick={() => handleClick(id, url)}>
                    {linkWord}
                  </button>,
                );

              if (part) nodes.push(part);
              return nodes;
            }),
      ),
    [agreementText],
  );

interface Props {
  agreementType: AgreementDetailType;
  forceNewTab?: boolean;
  css?: Styles;
  checked?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
}

export const PolicyAgreementCheckbox = ({ agreementType, forceNewTab, css: cssProps, ...checkboxProps }: Props) => {
  const { data: agreementDetails } = useQuery({
    queryKey: ['getAgreementDetailsApi'],
    queryFn: () => getAgreementDetailsApi({ agreementType }),
    select: (data) => data.data,
  });

  const overlay = useOverlay();
  const { isDesktop } = useBreakpoint();

  const handleClick = (id: string, url: string) => {
    if (forceNewTab || !isDesktop) {
      window.open(url, '_blank');
    } else {
      overlay.open(({ isOpen, onClose, unmount }) => (
        <PolicyInformationModal type={id} isOpen={isOpen} onClose={onClose} onExit={unmount} />
      ));
    }
  };

  if (!agreementDetails) return null;

  return (
    <Checkbox
      {...checkboxProps}
      css={{
        textStyle: 'Text/M/Regular',
        color: 'content.default.level-5',
        alignItems: 'flex-start',
        '& button': { textStyle: 'Text/L/Underline', color: 'content.accent.info' },
        ...cssProps,
      }}
    >
      {injectedText(agreementDetails, handleClick)}
    </Checkbox>
  );
};
